import { useEffect, useState } from 'react';
import { useAuth } from '@matix/auth';
import { heldOrdersAPI, receiptsAPI } from '@matix/api-client';
import { useUI } from './store/ui';
import { useTheme } from './store/theme';
import { useCart } from './store/cart';
import { useHeldOrders } from './store/heldOrders';
import { openHeldOrder } from './features/held-orders/openHeld';
import { ManagerPasswordModal } from './features/held-orders/ManagerPasswordModal';
import { OnlineOrderAlert, OnlineOrdersPanel, OnlineOrdersPoller } from './features/online-orders/OnlineOrderAlert';
import { useLang } from './lib/language';
import { loadTaxConfig } from './store/taxConfig';
import { loadInvoiceSources } from './store/invoiceSources';
import { loadOrderSettings } from './store/orderSettings';
import { loadTenantConfig } from './store/tenantConfig';
import { useLoyalty } from './store/loyalty';
import { useGiftCards } from './store/giftCards';
import { ShiftOpenScreen } from './features/shift/ShiftOpenScreen';
import { ShiftCloseScreen } from './features/shift/ShiftCloseScreen';
import { Sidebar } from './features/shell/Sidebar';
import { Topbar } from './features/shell/Topbar';
import { CatalogScreen } from './features/catalog/CatalogScreen';
import { ReportsScreen } from './features/reports/ReportsScreen';
import { OfflineQueueScreen } from './features/offline/OfflineQueueScreen';
import { useOfflineSync } from './features/offline/useOfflineSync';
import { CartPanel } from './features/cart/CartPanel';
import { CustomerPickerModal } from './features/modals/CustomerPickerModal';
import { TablePickerModal } from './features/modals/TablePickerModal';
import { SplitTransferModal } from './features/held-orders/SplitTransferModal';
import { TablesManualModal } from './features/held-orders/TablesManualModal';
import { PaymentModal } from './features/modals/PaymentModal';
import { usePaymentTypesWarm } from './features/modals/usePaymentTypes';
import { DiscountModal } from './features/modals/DiscountModal';
import { ModifierModal } from './features/modals/ModifierModal';
import { CashMovementModal } from './features/modals/CashMovementModal';
import { StockBalanceModal } from './features/modals/StockBalanceModal';
import { SellGiftCardModal } from './features/modals/SellGiftCardModal';
import { IssuedGiftCardsDialog } from './features/modals/IssuedGiftCardsDialog';
import { ZReportModal } from './features/shift/ZReportModal';
import { DeliveryConfirmModal } from './features/delivery/DeliveryConfirmModal';
import { MembershipModal } from './features/membership/MembershipModal';
import { CustomerOrdersModal } from './features/customers/CustomerOrdersModal';
import { useCustomerCall } from './features/customers/useCustomerCall';
import { ToastHost } from './components/Toast';
import { useEagerCatalogLoad } from './features/catalog/hooks';
import {
  usePrintConfig,
  useReprintReceipt,
  RecentReceiptToast,
} from './features/print';
import {
  useParkedOrdersStore,
  ParkedOrdersScreen,
} from './features/parked-orders';
import {
  ReceiptsScreen,
  ReceiptViewModal,
} from './features/receipts';
import {
  BranchMonitorScreen,
  PendingOrdersScreen,
} from './features/call-center';
import { DeliveryOrdersScreen } from './features/delivery-orders';
import { DeliveryManagerScreen } from './features/delivery-manager';
import { LaundryScreen } from './features/laundry';
import { ExpensesScreen } from './features/expenses';
import { TransfersScreen } from './features/transfers';
import { StockShortageDialog } from './features/stock/StockShortageDialog';
import { toast } from './store/toast';

/**
 * Top-level cashier app.
 *
 * Screen routing:
 *   - Not authenticated → login fullscreen
 *   - Authenticated, screen='shiftOpen' → shift-open fullscreen
 *   - Authenticated, screen='app' → main POS shell (sidebar + topbar + workspace)
 *     Inside the app shell, the active topbar tab decides what fills the
 *     workspace:
 *       'walkin' → catalog + cart (the normal selling view, for every order
 *                  mode — the mode itself is the topbar pill, not the tab)
 *       'orders' → parked orders screen (cart hidden)
 *   - Authenticated, screen='shiftClose' → shift-close fullscreen
 */
export function LegacyApp() {
  const { isAuthenticated, isLoading } = useAuth();
  const screen = useUI((s) => s.screen);
  const setScreen = useUI((s) => s.setScreen);
  usePrintConfig({ enabled: isAuthenticated });
  const cartCustomerId = useCart((s) => s.customer?.id ?? null);
  // Warm the payment-methods list at login so the payment modal's method grid
  // is instant on the first sale instead of waiting a round-trip on first open.
  usePaymentTypesWarm({ enabled: isAuthenticated });
  // Load any sales this till took while it had no network, and keep trying to
  // post them. Does nothing at all on a till that has never been offline.
  useOfflineSync(isAuthenticated);

  // Pull the tenant VAT config once after auth so the cart math + display
  // use the real per-subsidiary rate from mstr_TaxSetting (Option A — see
  // /api/v1/tenant/tax-config).
  useEffect(() => {
    if (isAuthenticated) void loadTaxConfig();
  }, [isAuthenticated]);

  // Pull the tenant's default order type once after auth so a fresh sale opens
  // on the order mode the admin configured (Settings → Order types).
  useEffect(() => {
    if (isAuthenticated) void loadOrderSettings();
  }, [isAuthenticated]);

  // Pull this branch's price lists (invoice sources). Empty on most tenants,
  // in which case the till never shows a picker and prices exactly as before.
  useEffect(() => {
    if (isAuthenticated) void loadInvoiceSources();
  }, [isAuthenticated]);

  // Pull the tenant POS feature flags (IsRestaurant / IsShowProductImage from
  // mstr_ConfigurationParameter) once after auth — they gate the dine-in/tables
  // flow and per-item product photos on the cashier.
  useEffect(() => {
    if (isAuthenticated) void loadTenantConfig();
  }, [isAuthenticated]);

  // Pull the loyalty programme once after auth. It decides whether the cart
  // shows a points badge and whether the payment modal offers redemption at
  // all; a tenant without loyalty gets the disabled default and no UI.
  useEffect(() => {
    if (isAuthenticated) void useLoyalty.getState().loadConfig();
  }, [isAuthenticated]);

  // Same for gift cards: the config decides whether the cart offers "Sell gift
  // card" and whether the payment modal offers a card as tender. A tenant
  // without gift cards gets the disabled default and no UI at all.
  useEffect(() => {
    if (isAuthenticated) void useGiftCards.getState().loadConfig();
  }, [isAuthenticated]);

  // Keep the attached customer's points in step with the cart. Selecting a
  // customer, swapping them, or clearing to a walk-in all re-resolve here, so
  // the balance on screen always belongs to the person being served.
  useEffect(() => {
    if (!isAuthenticated) return;
    void useLoyalty.getState().loadCustomer(cartCustomerId);
  }, [isAuthenticated, cartCustomerId]);


  // Apply theme to <html> on mount.
  const themeMode = useTheme((s) => s.theme);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  // The phone-system link (/customer-call?phoneNo=…) opens the caller once the till is up.
  useCustomerCall(isAuthenticated && screen === 'app');

  // When auth state changes, sync the screen accordingly.
  useEffect(() => {
    if (!isAuthenticated) {
      setScreen('login');
    } else if (screen === 'login') {
      setScreen('app');
    }
  }, [isAuthenticated, screen, setScreen]);

  // Refresh parked orders from the server whenever the cashier is signed
  // in. Reconciles localStorage with the server's truth: drops rows
  // deleted from another tab/station, retries pending POSTs, etc.
  useEffect(() => {
    if (!isAuthenticated) return;
    void useParkedOrdersStore.getState().refresh();
  }, [isAuthenticated]);

  if (isLoading && !isAuthenticated) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: 'var(--text-muted)',
        }}
      >
        Loading…
      </div>
    );
  }

  // LegacyApp only ever mounts inside App.tsx's 'session' branch (with a
  // valid token already seeded), so this should always be authenticated.
  // If the session expires, AuthProvider.onLogout resets the multi-tenant
  // store and App swaps us out for the login screen — render nothing in the
  // brief interim rather than the old (now-removed) duplicate login.
  if (!isAuthenticated) return null;

  return (
    <>
      {isAuthenticated && screen === 'shiftOpen' && <ShiftOpenScreen />}
      {isAuthenticated && screen === 'app' && <MainPosShell />}
      {isAuthenticated && screen === 'shiftClose' && <ShiftCloseScreen />}
      {isAuthenticated && screen === 'app' && <CustomerPickerModal />}
      {isAuthenticated && screen === 'app' && <TablePickerModal />}
      {isAuthenticated && screen === 'app' && <SplitTransferModal />}
      {isAuthenticated && screen === 'app' && <TablesManualModal />}
      {isAuthenticated && screen === 'app' && <PaymentModal />}
      {isAuthenticated && screen === 'app' && <DiscountModal />}
      {isAuthenticated && screen === 'app' && <ModifierModal />}
      {isAuthenticated && screen === 'app' && <CashMovementModal />}
      {isAuthenticated && screen === 'app' && <StockBalanceModal />}
      {isAuthenticated && screen === 'app' && <SellGiftCardModal />}
      {/* Card numbers and PINs arrive when the payment modal closes, so this
          dialog lives at app level rather than inside it. */}
      {isAuthenticated && <IssuedGiftCardsDialog />}
      {isAuthenticated && <ZReportModal />}
      {isAuthenticated && screen === 'app' && <DeliveryConfirmModal />}
      {isAuthenticated && screen === 'app' && <MembershipModal />}
      {isAuthenticated && screen === 'app' && <CustomerOrdersModal />}
      {isAuthenticated && <RecentReceiptToast />}
      {/* Above every other modal on purpose: a stock refusal can arrive while
          the payment modal is open, and that one must stay where it is. */}
      {isAuthenticated && screen === 'app' && <StockShortageDialog />}
      {/* The manager's password (LoginName 'Admin') — removing items from, or
          cancelling, a held order. Above the payment modal like the dialog above. */}
      {isAuthenticated && <ManagerPasswordModal />}
      {/* New web orders (WooCommerce, Ordable, …) at this branch: poll + pop-up + the bell's list. */}
      {isAuthenticated && screen === 'app' && <OnlineOrdersPoller />}
      {isAuthenticated && screen === 'app' && <OnlineOrdersPanel />}
      {isAuthenticated && screen === 'app' && <OnlineOrderAlert />}
      <ToastHost />
    </>
  );
}

/**
 * The main POS shell. Mounted only when the cashier is actively selling
 * (screen === 'app'). The eager catalog load fires here, NOT in App, so
 * we don't pay the sync cost on the login or shift-open screens.
 *
 * The workspace inside the shell is tab-driven: 'walkin' shows the catalog +
 * cart (for every order mode — walk-in, delivery, pickup…); "Open orders"
 * shows the parked orders screen.
 */
function MainPosShell() {
  useEagerCatalogLoad();

  // The held-orders badge counts server-held invoices too, so load them once
  // the till is up — not only when the Held Orders screen is first opened.
  useEffect(() => {
    void useHeldOrders.getState().refresh();
  }, []);

  // While a HELD order sits in this cart, tell the other tills (a heartbeat,
  // every minute; the server forgets a till that goes quiet for 3). Their
  // table picker then shows that table in the "open by another user" colour.
  // Released as soon as the cart lets it go — held, paid, cancelled, cleared.
  const heldOpenId = useCart((s) => (s.editingHeld ? s.editingReceiptId : null));
  useEffect(() => {
    if (heldOpenId == null) return undefined;
    const beat = () => void heldOrdersAPI.markOpen(heldOpenId).catch(() => {});
    beat();
    const timer = window.setInterval(beat, 60_000);
    return () => {
      window.clearInterval(timer);
      void heldOrdersAPI.releaseOpen(heldOpenId).catch(() => {});
    };
  }, [heldOpenId]);

  const tab = useUI((s) => s.tab);
  const setTab = useUI((s) => s.setTab);
  const hydrateFromParked = useCart((s) => s.hydrateFromParked);
  const handleReprint = useReprintReceipt();
  const { t: tr } = useLang();

  const [viewReceiptId, setViewReceiptId] = useState<number | null>(null);

  async function handleEdit(id: number): Promise<void> {
    try {
      const detail = await receiptsAPI.getById(id);
      if (!detail.can_edit) {
        toast(detail.edit_blocked_reason ?? 'This receipt cannot be edited', 'error');
        return;
      }
      // hydrateFromReceipt already puts the cart on the right order mode
      // (delivery vs takeaway) — we only need the selling view.
      useCart.getState().hydrateFromReceipt(detail);
      setTab('walkin');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load receipt';
      toast(msg, 'error');
    }
  }

  async function handleExchange(id: number): Promise<void> {
    try {
      const detail = await receiptsAPI.getById(id);
      // Load the invoice's items as return lines; the cashier trims quantities
      // (partial return) and/or adds new items (exchange), then charges.
      useCart.getState().hydrateForExchange(detail);
      setTab('walkin');
      toast('Loaded for return/exchange — adjust or add items, then charge', 'info');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load receipt';
      toast(msg, 'error');
    }
  }

  /**
   * Load a HELD invoice (IsHolded = 1) into the cart. Charging it then pays
   * that same invoice and Hold saves the changes onto it (usePlaceOrder /
   * holdCart read `editingHeld`). With `pay`, the payment modal opens at once.
   */
  async function handleOpenHeld(id: number, pay: boolean): Promise<void> {
    await openHeldOrder(id, { pay, tr });
  }

  async function handleRecall(id: string): Promise<void> {
    const snap = await useParkedOrdersStore.getState().recall(id);
    if (!snap) {
      toast('Could not recall that order. Try again.', 'error');
      return;
    }
    hydrateFromParked(snap);
    await useParkedOrdersStore.getState().consume(id);
    // The snapshot's order type is restored onto the cart by hydrateFromParked;
    // the tab only has to go back to the selling view.
    setTab('walkin');
  }

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <Topbar />
        <div className="workspace">
          {tab === 'orders' ? (
            <ParkedOrdersScreen
              onRecall={handleRecall}
              onOpenHeld={handleOpenHeld}
              onPrintHeld={(id) => void handleReprint(id)}
            />
          ) : tab === 'pending' ? (
            /* Call-centre orders for this branch. Its own workspace on
               purpose — a phone order is never dropped into the cart. */
            <PendingOrdersScreen />
          ) : tab === 'branchMonitor' ? (
            <BranchMonitorScreen />
          ) : tab === 'delivery' ? (
            /* Delivery hand-over. Reads receipts rather than cc_order, so a
               till-rung delivery order — which has no cc_order row and
               therefore never reaches Pending Orders — can still be found and
               paid for after the kitchen serves it. */
            <DeliveryOrdersScreen />
          ) : tab === 'laundry' ? (
            /* The laundry counter (module `laundry`). */
            <LaundryScreen />
          ) : tab === 'expenses' ? (
            /* Expense vouchers (legacy سند صرف, POS_AddEditExpance). */
            <ExpensesScreen />
          ) : tab === 'deliveryManager' ? (
            /* The legacy Delivery Manager board (/Delivery/Manager). */
            <DeliveryManagerScreen />
          ) : tab === 'receipts' ? (
            <ReceiptsScreen
              onView={(id) => setViewReceiptId(id)}
              onReprint={(id) => void handleReprint(id)}
              onEdit={(id) => void handleEdit(id)}
              onExchange={(id) => void handleExchange(id)}
            />
          ) : tab === 'reports' ? (
            <ReportsScreen />
          ) : tab === 'transfers' ? (
            <TransfersScreen />
          ) : tab === 'offline' ? (
            <OfflineQueueScreen />
          ) : (
            <>
              <CatalogScreen />
              <CartPanel />
            </>
          )}
        </div>
      </main>
      <ReceiptViewModal receiptId={viewReceiptId} onClose={() => setViewReceiptId(null)} />
    </div>
  );
}
